import React, { useEffect, useMemo, useState } from "react";
import { saveStudentPayment } from "../api/studentApi";
import { processRazorpayPayment } from "../utils/razorpayCheckout";
import { StudentInfo } from "../types";
import "../styles/MakePaymentForm.css";

interface MakePaymentFormProps {
  student: StudentInfo;
  lastValidityTo?: string | null;
  onCancel: () => void;
  onSuccess: () => void;
}

interface PaymentFormErrors {
  amount?: string;
  general?: string;
}

const MONTHLY_AMOUNT = 500;
const DAYS_PER_MONTH = 31;
const MIN_AMOUNT = 200;

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Local calendar date only (avoids UTC off-by-one on ISO strings). */
const parseDateOnly = (value: string): Date => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return parsed;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const startOfToday = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const daysBetween = (from: Date, to: Date): number => {
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return "-";
  const date = parseDateOnly(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const addDays = (dateStr: string, days: number): string => {
  const date = parseDateOnly(dateStr);
  date.setDate(date.getDate() + days);
  return formatDateInput(date);
};

/**
 * Validity From rule:
 * - Overdue by more than 14 days → today
 * - Otherwise (still valid, or overdue by 14 days or less) → last Validity To
 * - No previous payment → today
 */
const resolveValidityFrom = (lastValidityTo?: string | null): string => {
  const today = startOfToday();

  if (!lastValidityTo) {
    return formatDateInput(today);
  }

  const validityTo = parseDateOnly(lastValidityTo);
  if (Number.isNaN(validityTo.getTime())) {
    return formatDateInput(today);
  }

  const remainingDays = daysBetween(today, validityTo);
  const overdueDays = -remainingDays;

  // Overdue by more than 14 days → start from today
  if (overdueDays > 14) {
    return formatDateInput(today);
  }

  // Still valid, or overdue by 14 days or less → continue from last Validity To
  return formatDateInput(validityTo);
};

/** ₹500 = 31 days → days = amount * 31 / 500 */
const calculateGrantedDays = (amount: number): number => {
  if (!amount || amount <= MIN_AMOUNT) {
    return 0;
  }
  return Math.floor((amount * DAYS_PER_MONTH) / MONTHLY_AMOUNT);
};

const MakePaymentForm: React.FC<MakePaymentFormProps> = ({
  student,
  lastValidityTo,
  onCancel,
  onSuccess,
}) => {
  const validityFrom = useMemo(
    () => resolveValidityFrom(lastValidityTo),
    [lastValidityTo],
  );

  const [amount, setAmount] = useState("500");
  const [errors, setErrors] = useState<PaymentFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const amountValue = parseFloat(amount);
  const grantedDays = calculateGrantedDays(
    Number.isNaN(amountValue) ? 0 : amountValue,
  );
  const validityTo = grantedDays > 0 ? addDays(validityFrom, grantedDays) : "";
  const perDayRate = MONTHLY_AMOUNT / DAYS_PER_MONTH;

  const joiningDateLabel = student.dateOfJoining
    ? new Date(student.dateOfJoining).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "-";

  const fromDateRuleNote = useMemo(() => {
    if (!lastValidityTo) {
      return "No previous payment — Validity From is set to today.";
    }

    const end = parseDateOnly(lastValidityTo);
    if (Number.isNaN(end.getTime())) {
      return "Validity From is set to today.";
    }

    const remainingDays = daysBetween(startOfToday(), end);
    const overdueDays = -remainingDays;

    if (overdueDays > 14) {
      return "Overdue by more than 14 days — Validity From is set to today.";
    }

    if (overdueDays > 0) {
      return "Overdue by 14 days or less — Validity From is set to last Validity To.";
    }

    return "Still valid — Validity From is set to last Validity To.";
  }, [lastValidityTo]);

  useEffect(() => {
    if (errors.amount && amountValue > MIN_AMOUNT) {
      setErrors((prev) => ({ ...prev, amount: undefined }));
    }
  }, [amount, amountValue, errors.amount]);

  const validateForm = (): boolean => {
    const newErrors: PaymentFormErrors = {};

    if (!amount.trim()) {
      newErrors.amount = "Amount is required";
    } else if (Number.isNaN(amountValue) || amountValue <= MIN_AMOUNT) {
      newErrors.amount = `Amount must be greater than ₹${MIN_AMOUNT}`;
    } else if (grantedDays <= 0) {
      newErrors.amount = "Amount is too low to grant any days";
    }

    if (!validityFrom || !validityTo) {
      newErrors.general = "Unable to calculate validity dates.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    if (errors.amount) {
      setErrors((prev) => ({ ...prev, amount: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});

      await processRazorpayPayment({
        orderRequest: {
          studentDetailsId: student.studentDetailsId,
          amount: amountValue,
          validityFrom,
          validityTo,
          purpose: "membership",
        },
        description: "Membership Payment",
        prefill: {
          name: student.fullName,
          email: student.emailId,
          contact: student.mobileNumber,
        },
      });

      setIsSubmitted(true);
      setTimeout(() => {
        onSuccess();
      }, 1200);
    } catch (error) {
      setErrors({
        general:
          error instanceof Error
            ? error.message
            : "Failed to process payment. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="make-payment-page">
      <div className="make-payment-header">
        <div>
          <h1>Make Payment</h1>
          <p>Record membership payment for {student.fullName}</p>
        </div>
        <button type="button" className="back-btn" onClick={onCancel}>
          Back
        </button>
      </div>

      {isSubmitted && (
        <div className="success-banner">
          <h4>Payment Successful!</h4>
          <p>Your membership payment has been verified and saved.</p>
        </div>
      )}

      {errors.general && (
        <div className="error-banner">
          <h4>Error</h4>
          <p>{errors.general}</p>
        </div>
      )}

      <div className="make-payment-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Payment Details</h3>

            <div className="rate-note">
              Rate: ₹{MONTHLY_AMOUNT} = {DAYS_PER_MONTH} days (₹
              {perDayRate.toFixed(2)} / day). Minimum amount: greater than ₹
              {MIN_AMOUNT}.
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Student</label>
                <input
                  type="text"
                  value={`${student.fullName} - ${student.mobileNumber}`}
                  readOnly
                  className="readonly-field"
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount">
                  Amount (₹) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount"
                  min={MIN_AMOUNT + 1}
                  step="1"
                  className={errors.amount ? "error" : ""}
                  disabled={isSubmitting || isSubmitted}
                />
                {errors.amount && (
                  <span className="error-message">{errors.amount}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Joining</label>
                <input
                  type="text"
                  value={joiningDateLabel}
                  readOnly
                  className="readonly-field"
                  disabled
                />
              </div>

              <div className="form-group">
                <label>Granted Days</label>
                <input
                  type="text"
                  value={
                    grantedDays > 0
                      ? `${grantedDays} day${grantedDays !== 1 ? "s" : ""}`
                      : "Enter amount above ₹200"
                  }
                  readOnly
                  className="readonly-field"
                  disabled
                />
                <span className="field-note">
                  Calculated as Amount × {DAYS_PER_MONTH} ÷ ₹{MONTHLY_AMOUNT}
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Validity From Date</label>
                <input
                  type="text"
                  value={formatDisplayDate(validityFrom)}
                  readOnly
                  className="readonly-field"
                  disabled
                />
                <span className="field-note">{fromDateRuleNote}</span>
              </div>

              <div className="form-group">
                <label>Validity To Date</label>
                <input
                  type="text"
                  value={
                    validityTo
                      ? formatDisplayDate(validityTo)
                      : "Enter a valid amount"
                  }
                  readOnly
                  className="readonly-field"
                  disabled
                />
                <span className="field-note">
                  Auto-calculated from Validity From + granted days
                </span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || isSubmitted || grantedDays <= 0}
            >
              {isSubmitting ? "Opening payment..." : "Pay Now"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MakePaymentForm;
